/* oxlint-disable react/refs -- the handler is held in a ref so the subscription does not churn */
import { useRef } from 'react'
import { coerce } from 'app/lib/eventPatch'
import { useEventStream } from 'app/hooks/useEventStream'
import { MODEL_FIELD_TYPES } from 'app/generated/modelFieldTypes'
import type { MessageJson } from 'app/types/entities'

// The API publishes every Event row as one `event` frame; the model and action ride in the payload.
const STREAM_EVENT = `event`
// Created on every message save, updated for delivery status, read and archive changes.
const MESSAGE_ACTIONS = new Set([`created`, `updated`, `deleted`])

type EventPayload = {
  type: string
  action: string
  typeId: string
  data: Record<string, { to?: unknown }>
}

export type MessageEvent = {
  action: string
  /** The Message row rebuilt from the event changelog. Complete on `created`, a partial patch otherwise. */
  message: MessageJson & { id: string }
}

function toMessageEvent(payload: EventPayload): MessageEvent | undefined {
  if (payload.type !== `Message` || !MESSAGE_ACTIONS.has(payload.action)) return undefined
  const patch = coerce(payload.data, MODEL_FIELD_TYPES.Message ?? {})
  return { action: payload.action, message: { ...patch, id: payload.typeId } as MessageJson & { id: string } }
}

/**
 * Fires `handler` for every inbound/changed Message on the live stream, with the
 * message rebuilt from the event payload. A `created` event carries the whole
 * message, so surfaces that only need to know a message arrived can read it
 * straight from the event instead of refetching.
 *
 * The handler is held in a ref, so it always sees the latest render's values
 * without the subscription churning on every render.
 */
export function useMessageEvents(handler: (event: MessageEvent) => void, enabled = true): void {
  const handlerRef = useRef(handler)
  handlerRef.current = handler

  useEventStream(
    STREAM_EVENT,
    (event) => {
      const messageEvent = toMessageEvent(event.payload as EventPayload)
      if (messageEvent) handlerRef.current(messageEvent)
    },
    enabled,
  )
}
